import Text from "@/lib/components/ui/text/text";
import { isHostedLoginEnabled } from "@/lib/config";
import { useAuth, useAuthActions, useLoginWithRedirect } from "@frontegg/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// long enough to read the note, short enough that it never feels like a wait
const GOODBYE_MS = 1000;

/**
 * LogoutScreen owns /account/logout: it ends the Frontegg session, confirms it
 * on the login backdrop, and sends the user to the login page a second later.
 * Frontegg's own logout route moves aside in auth.tsx so this screen renders.
 */
export function LogoutScreen() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const { logout } = useAuthActions();
  const loginWithRedirect = useLoginWithRedirect();

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isAuthenticated) {
      logout();
      return;
    }
    const timer = setTimeout(() => {
      if (isHostedLoginEnabled) {
        loginWithRedirect();
        return;
      }
      window.location.href = "/account/login";
    }, GOODBYE_MS);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoading, logout, loginWithRedirect]);

  // the card echoes the login box: same gray, same border, same radius
  return (
    <div className="fixed inset-0 flex items-center justify-center p-6">
      <div
        role="status"
        className="w-full max-w-md rounded-2xl border border-border bg-background-sidebar p-12 text-center shadow-[0_8px_32px_rgb(0_0_0_/_0.35)]"
      >
        <Text variant="title" weight="semibold">
          {t("logout_title")}
        </Text>
        <Text className="text-foreground-pressed mt-1">{t("logout_note")}</Text>
      </div>
    </div>
  );
}
